import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { useAuth } from '../../Common/Configurations/AuthContext';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import './MyHotelBookingDetails.css';

const MyHotelBookingDetails = () => {
    const { hotelBookingId } = useParams();
    const [bookingDetails, setBookingDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const getBookingDetails = async () => {
            if (!hotelBookingId) {
                setError("Booking ID not found.");
                setLoading(false);
                return;
            }

            try {
                const response = await apiService.getById(config.GETHOTELBOOKINGDETAILS, hotelBookingId);                
                setBookingDetails(response.data);
            } catch (err) {
                setError("Failed to load booking details. Please try again.");
                console.error("Error fetching hotel booking details:", err);
            } finally {
                setLoading(false);
            }
        };

        getBookingDetails();
    }, [hotelBookingId]);

    const renderStatusBadge = (status) => {
        let badgeClass = 'bg-secondary';
        switch (status?.toUpperCase()) {
            case 'CONFIRMED':
            case 'SUCCESS':
                badgeClass = 'bg-success';
                break;
            case 'PENDING':
                badgeClass = 'bg-warning text-dark';
                break;
            case 'CANCELLED':
            case 'FAILED':
                badgeClass = 'bg-danger';
                break;
            default:
                break;
        }
        return <span className={`badge ${badgeClass}`}>{status || 'N/A'}</span>;
    };
    
    if (loading) {
        return <div className="text-center p-5"><div className="spinner-border"></div><p className="mt-2">Loading Booking Details...</p></div>;
    }

    if (error) {
        return <div className="container mt-4"><div className="alert alert-danger">{error}</div></div>;
    }

    if (!bookingDetails) {
        return <div className="container mt-4"><p>No booking details found for this ID.</p></div>;
    }
    
    const checkInDate = moment(bookingDetails.checkIn);
    const checkOutDate = moment(bookingDetails.checkOut);
    const numberOfNights = checkOutDate.diff(checkInDate, 'days');

    const taxesAndFees = (bookingDetails.gstAmount || 0) + (bookingDetails.convenienceFee || 0);

    const roomDetailsList = bookingDetails.hotelRoomDetails?.requestedHotels?.[0]?.roomDetails || [];

    return (
        <div className="main-content">
            <div className="booking-details-wrapper">
                <div className="container">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="details-title mb-0">Hotel Booking Details</h2>
                        <Link to="/my-hotelbookings" className="btn btn-outline-secondary">
                            <i className="ri-arrow-left-line me-2"></i>Back to Bookings
                        </Link>
                    </div>

                    <div className="card itinerary-card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">{bookingDetails.hotelDetails?.hotelName}</h5>
                            {renderStatusBadge(bookingDetails.bookingStatus)}
                        </div>
                        <div className="card-body">
                            <div className="stay-details-grid">
                                <div className="check-in-info">
                                    <small>Check-In</small>
                                    <strong>{checkInDate.format('ddd, DD MMM YYYY')}</strong>
                                    <span>From {bookingDetails.hotelDetails?.checkInTime}</span>
                                </div>
                                <div className="duration-info">
                                    <div className="line-with-icon">
                                        <i className="ri-moon-fill"></i>
                                    </div>
                                    <small>{numberOfNights} Night{numberOfNights > 1 ? 's' : ''}</small>
                                </div>
                                <div className="check-out-info">
                                    <small>Check-Out</small>
                                    <strong>{checkOutDate.format('ddd, DD MMM YYYY')}</strong>
                                    <span>Until {bookingDetails.hotelDetails?.checkOutTime}</span>
                                </div>
                                <div className="destination-info">
                                     <i className="ri-map-pin-line me-2"></i>
                                     <span>{bookingDetails.city}, {bookingDetails.country}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        <div className="col-lg-7">
                             <div className="card details-card mb-4">
                                <div className="card-header"><h6 className="mb-0">Guest(s) Details</h6></div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-borderless details-table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Guest Name</th>
                                                    <th>Type</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {bookingDetails.hotelGuestInfos?.map((roomGuests, roomIndex) => (
                                                    <React.Fragment key={roomIndex}>
                                                        {roomGuests.adultsFields.map((guest, guestIndex) => (
                                                            <tr key={`adult-${roomIndex}-${guestIndex}`}>
                                                                <td>{guestIndex + 1}</td>
                                                                <td>{guest.title}. {guest.firstName} {guest.lastName}</td>
                                                                <td><span className="badge bg-light text-dark">{guest.travelerType}</span></td>
                                                            </tr>
                                                        ))}
                                                         {roomGuests.kidsFields.map((guest, guestIndex) => (
                                                            <tr key={`kid-${roomIndex}-${guestIndex}`}>
                                                                <td>{guestIndex + 1}</td>
                                                                <td>{guest.title}. {guest.firstName} {guest.lastName}</td>
                                                                <td><span className="badge bg-light text-dark">{guest.travelerType}</span></td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="card details-card">
                                <div className="card-header"><h6 className="mb-0">Room(s) Details</h6></div>
                                 <div className="card-body p-0">
                                     <div className="table-responsive">
                                         <table className="table table-borderless details-table mb-0">
                                             <thead>
                                                 <tr>
                                                     <th>Room Type</th>
                                                     <th>Guests</th>
                                                     <th>Meal Plan</th>
                                                 </tr>
                                             </thead>
                                             <tbody>
                                                 {roomDetailsList.map((room, index) => (
                                                    <tr key={index}>
                                                        <td>{room.roomTypeName || 'N/A'}</td>
                                                        <td>{room.noOfAdults} Adult(s), {room.noOfChild} Kid(s)</td>
                                                        <td>{room.mealType || 'N/A'}</td>
                                                    </tr>
                                                 ))}
                                             </tbody>
                                         </table>
                                     </div>
                                 </div>
                            </div>
                        </div>

                        <div className="col-lg-5">
                            <div className="card details-card mb-4">
                                <div className="card-header"><h6 className="mb-0">Booking Summary</h6></div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between"><span>Booking No:</span><strong>{bookingDetails.bookingNo}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Booking Date:</span><strong>{moment(bookingDetails.bookingDate).format('DD MMM YYYY')}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Payment Status:</span><strong>{renderStatusBadge(bookingDetails.paymentStatus)}</strong></li>
                                        <li className="list-group-item">
                                            <span>Hotel Address:</span>
                                            {/* FIX: Use hotelDetails for address and city */}
                                            <strong className="d-block text-end">{bookingDetails.hotelDetails?.address}, {bookingDetails.hotelDetails?.cityName}</strong>
                                        </li>
                                        <li className="list-group-item d-flex justify-content-between">
                                            <span>Hotel Phone:</span>
                                            {/* FIX: Use hotelDetails for phone, fallback to traveller mobile */}
                                            <strong>{bookingDetails.hotelDetails?.phoneNumber || bookingDetails.travellerMobile || 'N/A'}</strong>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="card details-card">
                                <div className="card-header"><h6 className="mb-0">Fare Summary</h6></div>
                                <div className="card-body">
                                    <ul className="list-group list-group-flush">
                                        <li className="list-group-item d-flex justify-content-between"><span>Base Price:</span><strong>{Math.round(bookingDetails.hotelRoomDetails?.price || 0)} {bookingDetails.currency || 'INR'}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>Taxes & Fees:</span><strong>{Math.round(taxesAndFees)} {bookingDetails.currency || 'INR'}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between"><span>TCS:</span><strong>{Math.round(bookingDetails.tcsAmount || 0)} {bookingDetails.currency || 'INR'}</strong></li>
                                        <li className="list-group-item d-flex justify-content-between border-top pt-3 mt-2">
                                            <span className="fw-bold">Total Paid:</span>
                                            <strong className="text-primary fs-5">{Math.round(bookingDetails.totalPrice || 0)} {bookingDetails.currency || 'INR'}</strong>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyHotelBookingDetails;